import { AlertTriangle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAiCostStatus } from '@/hooks/useAi';

interface Props {
  className?: string;
}

/**
 * CostLimitBanner - Top-Banner wenn Tages-Cost > Soft-Limit (Sprint 6, WT-605).
 *
 * Banner ist Session-dismissable (sessionStorage-Key). Bei Reload wieder
 * sichtbar - wir wollen den User nicht "fuer immer" silencen, sondern nur
 * für die aktuelle Browser-Session.
 *
 * Wird nur auf KI-Routen rendered (in den Page-Bodies eingebunden), damit
 * normale Wartungs-UI nicht stören.
 */
export function CostLimitBanner({ className }: Props): JSX.Element | null {
  const { data } = useAiCostStatus();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = `ai-cost-banner-dismissed-${data?.date ?? 'today'}`;
    if (sessionStorage.getItem(key) === '1') setDismissed(true);
  }, [data?.date]);

  if (!data || !data.soft_limit_reached || dismissed) return null;

  const isHard = data.hard_limit_reached;
  const tone = isHard ? 'danger' : 'warning';
  const toneClasses: Record<string, string> = {
    warning: 'border-warning/30 bg-warning/10 text-warning',
    danger: 'border-danger/30 bg-danger/10 text-danger',
  };

  const onDismiss = (): void => {
    setDismissed(true);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(`ai-cost-banner-dismissed-${data.date}`, '1');
    }
  };

  return (
    <div
      role="alert"
      className={[
        'flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm',
        toneClasses[tone],
        className ?? '',
      ].join(' ')}
      data-testid="cost-limit-banner"
    >
      <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
      <span>
        <span className="font-medium">
          KI-Kosten heute: ${data.cost_today_usd.toFixed(2)}
        </span>{' '}
        {isHard ? (
          <>
            -{' '}
            <span className="font-medium">
              Hard-Limit (${data.daily_hard_limit_usd.toFixed(2)}) erreicht.
            </span>{' '}
            Weitere Vorschläge werden geblockt.
          </>
        ) : (
          <>
            - Soft-Limit (${data.daily_soft_limit_usd.toFixed(2)}) erreicht. Jeder
            weitere Vorschlag kostet ~$0.01.
          </>
        )}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Banner schließen"
        className="ml-auto rounded p-1 hover:bg-current/10"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
